import { SxProps, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import scrollHintTokens from "./ScrollHintRail.tokens";

const t = scrollHintTokens;

/** Positioning context for the two chevrons. */
export const railWrapperSx: SystemStyleObject<Theme> = {
  position: "relative",
  minWidth: 0,
};

/**
 * The scroller itself. Screens layer their own gap / padding / border on top;
 * this only owns the scrolling behaviour and the trailing reserve.
 *
 * Only the trailing side is reserved. A matching leading reserve would indent
 * the first item at rest and break its alignment with the block above, so the
 * start chevron overlays mid-rail content instead — which is what the fade is
 * for, and how scrollable tab strips generally behave.
 */
export const railScrollerSx: SystemStyleObject<Theme> = {
  display: "flex",
  minWidth: 0,
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  pr: { xs: `${t.width}px`, sm: 0 },
  // Keeps a focus ring from being clipped by the scroller's own overflow.
  py: "2px",
};

/**
 * Fade at whichever end still has content beyond it, so the strip reads as
 * continuing rather than cut off.
 */
export const railFadeSx = (
  side: "start" | "end",
  surface: string,
): SxProps<Theme> => ({
  display: { xs: "block", sm: "none" },
  position: "absolute",
  top: 0,
  bottom: 0,
  [side === "start" ? "left" : "right"]: 0,
  width: t.fadeWidth,
  pointerEvents: "none",
  background: `linear-gradient(to ${side === "start" ? "left" : "right"}, ${
    t.transparent
  } 0%, ${surface} 62%)`,
});

/** The chevron itself — a real button, so it works without a swipe. */
export const railButtonSx = (
  side: "start" | "end",
  surface: string,
): SxProps<Theme> => ({
  display: { xs: "inline-flex", sm: "none" },
  position: "absolute",
  [side === "start" ? "left" : "right"]: 0,
  top: "50%",
  transform: "translateY(-50%)",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  p: 0,
  minWidth: t.buttonSize,
  width: t.buttonSize,
  height: t.buttonSize,
  borderRadius: t.radius,
  bgcolor: surface,
  border: `1px solid ${t.border}`,
  color: t.color,
  boxShadow: t.shadow,
  "&:hover": { bgcolor: surface },
});

/** One chevron path, turned round for the leading end. */
export const railChevronSx = (side: "start" | "end"): SxProps<Theme> => ({
  width: t.iconSize,
  height: t.iconSize,
  transform: side === "start" ? "rotate(180deg)" : undefined,
});
